/**
 * RBAC Context Provider (메뉴 역할세트 기반)
 *
 * 데이터 소스: GET /me/menus(menu_key 배열) + 프로필 superAdmin 플래그.
 * 레거시 /rbac/users/* 권한 모델은 제거되었으며, 관리 메뉴를 포함한 모든 메뉴 접근은
 * menuKeys(역할세트) 포함 여부 기준으로 판정한다(superAdmin은 전체 허용).
 */

'use client';

import { menuRoleSetClient } from '@/lib/menu-role-set-client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth-context';

export interface RBACContextType {
  // 메뉴 접근 데이터
  menuKeys: string[];

  // 관리자 플래그 (auth-context 재노출)
  isSuperAdmin: boolean;

  // 접근 판정
  canAccessMenu: (menuId: string) => boolean;

  // 역할 검사 (토큰 기반 위임 — 레거시 호환)
  hasRole: (roleCode: string) => boolean;
  hasAnyRole: (roleCodes: string[]) => boolean;

  // 상태
  loading: boolean;
  error: string | null;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within a RBACProvider');
  }
  return context;
};

interface RBACProviderProps {
  children: React.ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const {
    isAuthenticated,
    isSuperAdmin,
    hasRole,
    hasAnyRole,
    getCurrentUserId,
  } = useAuth();
  const currentUserId = getCurrentUserId();

  const [menuKeys, setMenuKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 로그인 후(currentUserId 확보 시) /me/menus 1회 페치
  useEffect(() => {
    if (!isAuthenticated || currentUserId == null) {
      setMenuKeys([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    menuRoleSetClient
      .getMyMenus()
      .then(keys => {
        if (!cancelled) {
          setMenuKeys(keys);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setMenuKeys([]);
          setError(err instanceof Error ? err.message : '메뉴 권한 조회 실패');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, currentUserId]);

  // 메뉴 접근 판정: SUPER_ADMIN은 전체, 그 외는 menuKeys 포함 여부
  // 참고(스펙 §7): 데이터 API 403 시 menuKeys 재조회/실시간 푸시는 1차 범위 밖.
  const canAccessMenu = useCallback(
    (menuId: string): boolean => {
      if (!isAuthenticated) {
        return false;
      }
      return isSuperAdmin || menuKeys.includes(menuId);
    },
    [isAuthenticated, isSuperAdmin, menuKeys]
  );

  const contextValue: RBACContextType = useMemo(
    () => ({
      menuKeys,
      isSuperAdmin,
      canAccessMenu,
      hasRole,
      hasAnyRole,
      loading,
      error,
    }),
    [menuKeys, isSuperAdmin, canAccessMenu, hasRole, hasAnyRole, loading, error]
  );

  return <RBACContext.Provider value={contextValue}>{children}</RBACContext.Provider>;
};
